// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import { OrderUploadErrorsModalStyles } from "@insite/content-library/Components/OrderUploadErrorsModal";
import { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface MyListsDetailsOptionsStyles {
    wrapper?: InjectableCss;
    leftColumnWrapper?: InjectableCss;
    rightColumnWrapper?: InjectableCss;
    container?: InjectableCss;
    selectAllGridItem?: InjectableCss;
    selectAll?: CheckboxProps;
    editSortOrder?: CheckboxProps;
    searchGridItem?: InjectableCss;
    addItemsGridItem?: InjectableCss;
    addItemsLink?: LinkPresentationProps;
    printAllQrCodes?: LinkPresentationProps;
    addItemsGridContainer?: InjectableCss;
    productSelectorGridItem?: InjectableCss;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: InjectableCss;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    search?: TextFieldProps;
    sortByGridItem?: InjectableCss;
    sortByOverflowMenu?: OverflowMenuProps;
    sortByClickable?: ClickablePresentationProps;
    selectedSortByClickable?: ClickablePresentationProps;
    closeAddItemsToList?: IconProps;
    copyWrapper?: InjectableCss;
    stepWrapper?: InjectableCss;
    groupsWrapper?: InjectableCss;
    groupItem?: ClickablePresentationProps;
    addNewGroupItem?: InjectableCss;
    addNewButton?: ButtonPresentationProps;
    manageListsLink?: LinkPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    newListInput?: TextFieldProps;
    selectSortOption?: SelectPresentationProps;
    sortOptionWrapper?: InjectableCss;
    createNewGroupWrapper?: InjectableCss;
    createNewGroup?: LinkPresentationProps;
    actionsPopup?: InjectableCss;
    notifyStyle?: InjectableCss;
}

export const myListsDetailsOptionsStyles: MyListsDetailsOptionsStyles = {
    wrapper: {
        css: css`
            @media print {
                display: none;
            }
            margin-top: 50px;
            margin-bottom: 15px;
            width: 100%;
        `,
    },
    leftColumnWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    sortOptionWrapper: {
        css: css`
            margin-right: 30px;
            @media (max-width: 964px) {
                margin-top: 10px;
            }
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    selectSortOption: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                width: 228px;

                label {
                    width: auto;
                    font-weight: bold;
                    font-size: 14px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                @media (max-width: 481px) {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                font-size: 14px;
                height: 32px;
                font-weight: 500;
            `,
        },
        iconProps: {
            css: css`
                &&&&&& {
                    right: 0;
                    top: 0;
                }
            `,
        },
    },
    container: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            position: relative;
            @media (max-width: 519px) {
                flex-direction: column;
                align-items: flex-start;
                > * {
                    margin-bottom: 10px;
                    margin-top: 10px;
                }
            }
        `,
    },
    selectAllGridItem: {
        css: css`
            margin-right: 25px;
            @media (max-width: 650px) {
                margin: 0;
                order: -1;
                margin-bottom: 15px;
            }
        `,
    },
    selectAll: {
        css: css`
            cursor: pointer;
        `,
    },
    editSortOrder: {
        css: css`
            cursor: pointer;
            margin-left: 30px;
        `,
    },
    searchGridItem: {
        css: css`
            margin-right: 30px;
            @media (max-width: 481px) {
                margin-right: 0;
                width: 100%;
            }
        `,
    },
    addItemsGridItem: {
        css: css`
            display: flex;
            align-items: flex-end;
            @media (max-width: 686px) {
                margin-top: 10px;
                margin-right: 30px;
            }
        `,
    },
    addItemsLink: linkIconStyleGenerator("AddToList", "15px", 12, "margin-right: 30px; font-weight: 500;"),
    printAllQrCodes: linkIconStyleGenerator(
        "QrCode",
        "15px",
        22,
        "opacity: 0.8; font-weight: 500;",
        "",
        `
        @media (max-width: 1157px) {
            margin-top: 10px;
            margin-right: 30px;
        }
    `,
    ),
    createNewGroup: linkIconStyleGenerator("CreateGroup", "15px", 14, "opacity: 0.8; font-weight: 500;"),
    createNewGroupWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            @media (max-width: 1314px) {
                justify-content: flex-start;
                margin-top: 10px;
            }
        `,
    },

    productSelectorGridItem: {
        css: css`
            display: flex;
            @media (max-width: 680px) {
                flex-wrap: wrap;
            }
        `,
    },
    productSelectorStyles: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                padding: 0;
                width: 100%;
                align-items: flex-start;
            `,
        },
        searchWrapper: {
            css: css`
                max-width: 450px;
                width: 100%;
                margin-right: 30px;
            `,
        },
        searchDynamicDropdown: {
            cssOverrides: {
                formField: css`
                    flex-direction: row;
                    align-items: center;
                    width: 100%;

                    label {
                        width: auto;
                        font-weight: bold;
                        font-size: 14px;
                        margin-right: 8px;
                        white-space: nowrap;
                    }
                    @media (max-width: 650px) {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                `,
                formInputWrapper: css`
                    margin-top: 0 !important;
                `,
                inputSelect: css`
                    font-size: 14px;
                    height: 32px;
                `,
                selectedText: css`
                    padding: 5px 10px;
                `,
                list: css`
                    top: 32px;
                    width: 100%;
                    border-top: 0;
                `,
            },
            iconProps: {
                css: css`
                    &&&&&& {
                        right: 0;
                        top: 0;
                    }
                `,
            },
        },
        qtyWrapper: {
            css: css`
                display: flex;
                align-items: center;
                width: 160px;
                padding: 0;
                margin-right: 30px;

                &&& > label {
                    width: auto;
                    font-weight: bold;
                    font-size: 14px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                label + div {
                    margin-top: 0;
                    height: 32px;
                }

                &&& input {
                    height: 32px !important;
                }
                ${({ theme }: { theme: any }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex: unset;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
        selectButton: {
            css: css`
                min-height: 32px !important;
            `,
        },
        buttonWrapper: {
            css: css`
                > button {
                    margin-top: 0 !important;
                }
                ${({ theme }: { theme: any }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex: 0 1 100%;
                            margin-top: 15px;
                            button {
                                width: 100%;
                            }
                        `,
                        null,
                        css`
                            margin-top: 15px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    } as ProductSelectorStyles,
    uploadItemsLink: {
        css: css`
            margin: 0;
            white-space: nowrap;
            margin-right: 15px;
            height: 32px;
        `,
    },
    search: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                width: 292px;

                label {
                    width: auto;
                    font-weight: bold;
                    font-size: 14px;
                    margin-right: 8px;
                    white-space: nowrap;
                }
                @media (max-width: 481px) {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                padding-left: 32px !important;
                font-size: 14px;
                height: 32px;
            `,
        },
        iconProps: {
            src: "Search",
            size: 14,
            color: "common.ui1",
            css: css`
                &&&&&& {
                    padding: 0;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    svg {
                        opacity: 0.8;
                        max-height: 14px;
                        max-width: 14px;
                    }
                }
            `,
        },
    },
    sortByOverflowMenu: {
        iconProps: { src: "ChevronsUpDown" },
    },
    selectedSortByClickable: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    closeAddItemsToList: {
        src: "X",
        css: css`
            margin: 0;
            margin-top: 5px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },

    /**  Create New Goup/Select Group For Product  */
    copyWrapper: {
        css: css`
            padding-bottom: 20px;
            margin-bottom: 25px;
            border-bottom: 1px solid ${getColor("secondary")};
        `,
    },
    stepWrapper: {
        css: css`
            height: 0;
            overflow: hidden;
            opacity: 0;
            position: absolute;
            right: -100%;
            transition: all 400ms;

            &&.in-view {
                height: auto;
                overflow-y: auto;
                opacity: 1;
                position: static;
                transition: all 200ms;
            }
        `,
    },
    descriptionText: {
        as: "p",
        size: "15px",
        lineHeight: "22px",
    },
    groupsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-right: -30px;
            @media (max-width: 760px) {
                margin-right: -10px;
            }
        `,
    },
    newListInput: {
        cssOverrides: {
            formField: css`
                max-width: 540px;
                label {
                    font-weight: 500;
                }
            `,
        },
    },
    groupItem: {
        css: css`
            width: calc(100% / 4 - 30px);
            margin-right: 30px;
            text-align: center;
            display: block;
            margin-top: 35px;

            .item-wrapper {
                border: 1px solid transparent;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            &&.selected .item-wrapper {
                border-color: ${getColor("primary")};
            }

            @media (max-width: 760px) {
                width: calc(100% / 2 - 10px);
                margin-right: 10px;
            }
        `,
    },
    addNewGroupItem: {
        css: css`
            width: 100%;
            height: 162px;
            border: 2px dashed ${getColor("common.ui3")};
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 15px;
        `,
    },
    addNewButton: {
        css: css`
            padding: 0;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            &&:hover {
                svg polygon {
                    fill: ${getColor("common.white")};
                }
            }
        `,
    },
    manageListsLink: {
        css: css`
            flex-grow: 1;
            justify-content: flex-end;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-right: 10px;
        `,
    },
    actionsPopup: {
        css: css`
            position: absolute;
            right: 0;
            @media (max-width: 481px) {
                top: -20px;
            }
            nav {
                height: 34px;
                width: 34px;
            }
            nav > button:first-child {
                min-height: 34px;
                height: 34px;
                width: 34px;
            }
        `,
    },
    notifyStyle: {
        css: css`
            margin-top: 15px;
            width: fit-content;
            max-width: 100%;
            min-width: 540px;
        `,
    },
};
